import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native';

import { Box, Text } from '@components/Restyle';
import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import Icon from '@components/shared/Icon/Icon';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import {
  useCreateShareLinkMutation,
  Document,
  LocalFile,
} from '@graphql/generated';
import { useShareImagesLink } from '@hooks/useShareImagesLink.web';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  ClipboardFailureReason,
  copyTextToClipboard,
} from '@utils/copyTextToClipboard';
import {
  filterShareableDocuments,
  getDocumentIds,
} from '@utils/getDocumentIds';

type FileShareLinkProps = {
  selectedDocuments: Array<Document | LocalFile>;
};

export const FileShareLink: React.FC<FileShareLinkProps> = ({
  selectedDocuments,
}) => {
  const [createShareLink, { loading }] = useCreateShareLinkMutation();
  const { onShareImages } = useShareImagesLink();
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  // Same red-icon failure toast as MediaViewer's copy-link (TA-10845).
  const copyFailedToastRef = useRef<BottomToastHandle>(null);

  const showCopyFailedToast = (reason: ClipboardFailureReason) => {
    copyFailedToastRef.current?.show(
      t(CLIPBOARD_FAILURE_MESSAGE_KEYS[reason]),
      { duration: 3000, icon: 'warning' }
    );
  };
  const [linkHovered, setLinkHovered] = useState(false);

  const copyToClipboard = async (link: string) => {
    if (!link) {
      showCopyFailedToast('link-generation-failed');
      return;
    }
    const result = await copyTextToClipboard(link);
    if (!result.success) {
      showCopyFailedToast(result.reason);
      return;
    }
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const handleSharePress = async () => {
    if (loading) return;
    const isMedia = selectedDocuments.every(
      (item) => item.isImage || item.isVideo
    );
    if (isMedia) {
      const url = await onShareImages(selectedDocuments);
      // onShareImages returns null on failure — surface it (TA-10845).
      await copyToClipboard(url ?? '');
    } else if (typeof navigator?.share === 'function') {
      // Same LocalFile-truncation risk as the isMedia branch above — exclude
      // anything that isn't a persisted Document before sharing. — TA-10212
      const shareableDocuments = filterShareableDocuments(selectedDocuments);
      if (shareableDocuments.length === 0) return;
      const documentId = getDocumentIds(shareableDocuments);

      try {
        // Generate share link
        await createShareLink({
          variables: {
            documentIds: documentId,
          },
          onCompleted: ({ createShareLink }) => {
            // A missing URL routes through copyToClipboard's
            // link-generation-failed toast instead of failing silently.
            copyToClipboard(createShareLink?.sharedUrl ?? '');
          },
        });
      } catch (error) {
        console.error('Error generating share link:', error);
        showCopyFailedToast('link-generation-failed');
      }
    }
  };

  return (
    <Box position='relative'>
      {/* Main tooltip for "Copy Link to Invite" */}
      <TooltipWebV3
        variant={copied ? 'light' : 'dark'}
        marginOffset={8}
        open={linkHovered}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        title={
          copied ? (
            <Box flexDirection='row'>
              <Icon name='CheckCircle' color='greenSecondary' variant='s' />
              <Text
                ml='xs'
                accessibilityLabel='Copy link'
                variant='labelSmall'
                color='black'>
                Link copied!
              </Text>
            </Box>
          ) : (
            <Text
              ml='xxs'
              accessibilityLabel='Copy link'
              variant='labelSmall'
              color='white'>
              Copy Link
            </Text>
          )
        }
        placement='top'>
        {/* IMPORTANT: Tooltip child must be a DOM element (div/span),
            so that MUI can attach ref and props to the same element. */}
        <div
          onMouseEnter={() => setLinkHovered(true)}
          onMouseLeave={() => setLinkHovered(false)}
          // Make sure cursor shows pointer when clickable
          style={{ display: 'inline-flex', cursor: 'pointer' }}
          onClick={handleSharePress}>
          {loading ? (
            <Box width={24}>
              <ActivityIndicator color='white' />
            </Box>
          ) : (
            <Box padding='xxs' borderRadius='xs'>
              {/* White-icon-on-dark hover (TA-9005): white overlay at 25%,
                  same treatment as WebIcon's isWhiteIcon. */}
              {linkHovered && (
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                  backgroundColor='white'
                  opacity={0.25}
                  borderRadius='xs'
                />
              )}
              <Icon
                name='LinkNew'
                color='white'
                variant='l'
                onPress={handleSharePress}
              />
            </Box>
          )}
        </div>
      </TooltipWebV3>
      <BottomToast ref={copyFailedToastRef} />
    </Box>
  );
};

FileShareLink.displayName = 'FileShareLink';
